import { HoverCard, Text, Tooltip } from "@mantine/core";
import { IconAlertCircle, IconAlertTriangle } from "@tabler/icons-react";
import clsx from "clsx";
import { type JSX } from "react";

import { useSession } from "@/sdk";
import { formatTime, useStopwatch } from "@/shared-utils";

import type { TaskProps } from "../../types";

import { useIsPacingActive } from "../../hooks/useIsPacingActive";
import { usePacingPulseSync } from "../../hooks/usePacingPulseSync";
import { type PacingVisualState, usePacingState } from "../../hooks/usePacingState";
import classes from "./PacingCountdownPill.module.css";
import { PacingPopoverContent } from "./PacingPopover";

/** Remaining time as (-)mm:ss — negative once the countdown passes the target. */
function formatCountdown(remainingSeconds: number): string {
    const sign = remainingSeconds < 0 ? "-" : "";
    return `${sign}${formatTime(Math.abs(remainingSeconds), false)}`;
}

/** Wording matches the Expert Hub cards. */
function getStatusTag(state: PacingVisualState): { label: string; Icon: typeof IconAlertTriangle } | null {
    if (state === "exceeded") return { label: "Off track", Icon: IconAlertCircle };
    if (state === "approaching") return { label: "At risk", Icon: IconAlertTriangle };
    return null;
}

/**
 * Plain count-up shown until a target exists, as the current pill does. Split out so `useStopwatch` (1 Hz) mounts
 * only here — the countdown path renders `pacing.remainingSeconds` and would re-render for nothing.
 */
function NeutralPill(): JSX.Element {
    const { startedTimestamp, kind } = useSession();
    const formattedSessionDuration = useStopwatch(startedTimestamp);
    const totalLabel = `Total ${kind === "with-customer" ? formattedSessionDuration : "--"}`;

    return (
        <Tooltip label={totalLabel} position="bottom" zIndex="var(--mantine-priority-higher)">
            <div className={clsx(classes.pill, classes.neutral)} data-testid="pacing-countdown-pill">
                <span className={classes.soloSegment}>
                    <Text className={classes.countdown} component="span" size="xs" ff="monospace">
                        {formattedSessionDuration}
                    </Text>
                </span>
            </div>
        </Tooltip>
    );
}

/**
 * VCP 1.5 "Glow Bar" timer, replacing the current count-up pill: call type inline, a status tag once at risk,
 * and time remaining counting DOWN past zero. The sole pacing-analytics emitter (CallBarGlow, the
 * other subscriber, passes emitAnalytics=false), so threshold events fire exactly once.
 */
export function PacingCountdownPill({ task }: TaskProps): JSX.Element {
    const isPacingActive = useIsPacingActive();
    const { startedTimestamp, id: sessionId } = useSession();
    const callActive = task.status === "assigned";

    const pacing = usePacingState({
        partner: task.partner,
        sessionId,
        startedTimestamp,
        callActive,
        isPacingActive,
    });

    const pulseSyncRef = usePacingPulseSync(pacing.state === "approaching" || pacing.state === "exceeded");

    if (!pacing.hasTarget) {
        return <NeutralPill />;
    }

    const statusTag = getStatusTag(pacing.state);
    // Before GAIA classifies an onTrack call there is neither, and an empty label segment is ~22px
    // of dead padding behind a stray divider.
    const hasLabelContent = Boolean(pacing.displayCategory) || statusTag !== null;

    const pillContent = (
        <div
            ref={pulseSyncRef}
            className={clsx(classes.pill, classes[pacing.state])}
            data-testid="pacing-countdown-pill"
        >
            {hasLabelContent ? (
                <span className={classes.labelSegment}>
                    {pacing.displayCategory ? (
                        <Text className={classes.category} component="span" size="xs" data-testid="pacing-call-type">
                            {pacing.displayCategory}
                        </Text>
                    ) : null}
                    {statusTag ? (
                        <span className={classes.statusTag} data-testid="pacing-status-tag">
                            <statusTag.Icon size={10} stroke={2} aria-hidden />
                            {statusTag.label}
                        </span>
                    ) : null}
                </span>
            ) : null}
            <span className={classes.timerSegment}>
                <Text
                    className={classes.countdown}
                    component="span"
                    size="xs"
                    ff="monospace"
                    data-testid="pacing-countdown"
                >
                    {formatCountdown(pacing.remainingSeconds)}
                </Text>
            </span>
        </div>
    );

    return (
        <HoverCard
            position="bottom"
            withArrow
            shadow="md"
            withinPortal
            openDelay={200}
            closeDelay={100}
            zIndex="var(--mantine-priority-higher)"
        >
            <HoverCard.Target>{pillContent}</HoverCard.Target>
            <HoverCard.Dropdown>
                <PacingPopoverContent pacing={pacing} />
            </HoverCard.Dropdown>
        </HoverCard>
    );
}
